// The device view's controls, drawn the way Live draws them: a rotary knob
// dragged up and down, a lit toggle, a step select. Each reports a DRAFT
// while it moves (the lane hears it at once) and COMMITS on release — one
// proposal per gesture, never one per pixel.

import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import { cn } from "@web-shared/lib/utils";

export interface KnobProps {
  label: string;
  /** In display units. */
  value: number;
  min: number;
  max: number;
  /** Sweep the range logarithmically (frequencies, times). Needs min > 0. */
  log?: boolean;
  /** Snap to whole steps. */
  step?: number;
  format: (v: number) => string;
  disabled?: boolean;
  /** The arc from the middle rather than from the left (gains, pans). */
  bipolar?: boolean;
  onDraft: (v: number) => void;
  onCommit: (v: number) => void;
  size?: number;
  accent?: string;
}

function toPos(v: number, min: number, max: number, log: boolean): number {
  if (!(max > min)) return 0;
  if (log && min > 0) return Math.max(0, Math.min(1, Math.log(Math.max(min, Math.min(max, v)) / min) / Math.log(max / min)));
  return Math.max(0, Math.min(1, (v - min) / (max - min)));
}

function fromPos(t: number, min: number, max: number, log: boolean, step?: number): number {
  const c = Math.max(0, Math.min(1, t));
  const v = log && min > 0 ? min * Math.pow(max / min, c) : min + c * (max - min);
  return step ? Math.round(v / step) * step : v;
}

const ARC = 270;

function arcPath(cx: number, cy: number, r: number, a0: number, a1: number): string {
  const rad = (a: number) => ((a - 90) * Math.PI) / 180;
  const x0 = cx + r * Math.cos(rad(a0));
  const y0 = cy + r * Math.sin(rad(a0));
  const x1 = cx + r * Math.cos(rad(a1));
  const y1 = cy + r * Math.sin(rad(a1));
  const large = Math.abs(a1 - a0) > 180 ? 1 : 0;
  const sweep = a1 > a0 ? 1 : 0;
  return `M ${x0} ${y0} A ${r} ${r} 0 ${large} ${sweep} ${x1} ${y1}`;
}

export function Knob({ label, value, min, max, log = false, step, format, disabled, bipolar, onDraft, onCommit, size = 34, accent = "var(--primary)" }: KnobProps) {
  const [draft, setDraft] = useState<number | null>(null);
  const drag = useRef<{ y: number; pos: number } | null>(null);
  useEffect(() => {
    if (!drag.current) setDraft(null);
  }, [value]);
  const shown = draft ?? value;
  const pos = toPos(shown, min, max, log);
  const start = -ARC / 2;
  const angle = start + pos * ARC;
  const zero = bipolar ? 0 : start;
  const r = size / 2 - 3;
  const c = size / 2;

  const move = (next: number) => {
    const v = fromPos(next, min, max, log, step);
    setDraft(v);
    onDraft(v);
  };
  const commit = () => {
    drag.current = null;
    if (draft === null) return;
    const v = draft;
    if (Math.abs(v - value) > 1e-9) onCommit(v);
    // A commit the room never echoes (refused, or a no-op once clamped)
    // must not leave the knob showing a value nobody holds.
    window.setTimeout(() => {
      if (!drag.current) setDraft(null);
    }, 1500);
  };
  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { y: e.clientY, pos };
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    const range = e.shiftKey ? 800 : 160;
    move(d.pos + (d.y - e.clientY) / range);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (disabled) return;
    const delta = e.key === "ArrowUp" || e.key === "ArrowRight" ? 1 : e.key === "ArrowDown" || e.key === "ArrowLeft" ? -1 : 0;
    if (!delta) return;
    e.preventDefault();
    const inc = step ? toPos(min + step, min, max, false) : e.shiftKey ? 0.002 : 0.02;
    move(toPos(draft ?? value, min, max, log) + delta * Math.max(inc, 0.002));
  };
  return (
    <div className={cn("flex w-[52px] shrink-0 flex-col items-center gap-0.5 select-none", disabled && "opacity-50")} title={`${label}: ${format(shown)}`}>
      <span className="w-full truncate text-center text-[9px] leading-tight text-muted-foreground">{label}</span>
      <div
        role="slider"
        tabIndex={disabled ? -1 : 0}
        aria-label={label}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={shown}
        aria-valuetext={format(shown)}
        aria-disabled={disabled}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={commit}
        onPointerCancel={commit}
        onKeyDown={onKeyDown}
        onKeyUp={(e) => {
          e.stopPropagation();
          if (e.key.startsWith("Arrow")) commit();
        }}
        onBlur={() => drag.current === null && draft !== null && commit()}
        className={cn("touch-none rounded-full outline-none focus-visible:ring-1 focus-visible:ring-primary", !disabled && "cursor-ns-resize")}
        style={{ width: size, height: size }}
      >
        <svg width={size} height={size} aria-hidden>
          <path d={arcPath(c, c, r, start, start + ARC)} fill="none" stroke="var(--control-line)" strokeWidth={3} strokeLinecap="round" />
          {Math.abs(angle - zero) > 0.5 && <path d={arcPath(c, c, r, Math.min(zero, angle), Math.max(zero, angle))} fill="none" stroke={accent} strokeWidth={3} strokeLinecap="round" />}
          <circle cx={c} cy={c} r={r - 5} fill="var(--control)" />
          <line
            x1={c}
            y1={c}
            x2={c + (r - 6) * Math.cos(((angle - 90) * Math.PI) / 180)}
            y2={c + (r - 6) * Math.sin(((angle - 90) * Math.PI) / 180)}
            stroke="var(--foreground)"
            strokeWidth={2}
            strokeLinecap="round"
          />
        </svg>
      </div>
      <span className="tabular w-full truncate text-center text-[9px] leading-tight">{format(shown)}</span>
    </div>
  );
}

/** A lit switch — Live's yellow-when-on button. */
export function Toggle({ label, on, disabled, onChange, title }: { label: string; on: boolean; disabled?: boolean; onChange: (on: boolean) => void; title?: string }) {
  return (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={on}
      title={title ?? label}
      onClick={() => onChange(!on)}
      className={cn(
        "h-5 shrink-0 rounded px-1.5 text-[9px] font-semibold uppercase tracking-wide transition-colors disabled:opacity-50",
        on ? "bg-[var(--changed)] text-black" : "bg-[var(--control)] text-muted-foreground hover:text-foreground",
      )}
    >
      {label}
    </button>
  );
}

/** A quantized choice by name. */
export function StepSelect({ label, value, options, disabled, onChange }: { label: string; value: number; options: Array<{ value: number; label: string }>; disabled?: boolean; onChange: (v: number) => void }) {
  return (
    <label className={cn("flex min-w-0 flex-col gap-0.5 text-[9px] text-muted-foreground", disabled && "opacity-50")}>
      <span className="truncate">{label}</span>
      <select
        className="h-5 min-w-0 rounded border border-[var(--control-line)] bg-[var(--control)] px-1 text-[10px] text-foreground"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        onKeyDown={(e) => e.stopPropagation()}
        aria-label={label}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

// ---- formatting -------------------------------------------------------------

export function fmtHz(hz: number): string {
  return hz >= 1000 ? `${(hz / 1000).toFixed(hz >= 10000 ? 1 : 2)} kHz` : `${hz.toFixed(hz >= 100 ? 0 : 1)} Hz`;
}

export function fmtMs(ms: number): string {
  return ms >= 1000 ? `${(ms / 1000).toFixed(2)} s` : `${ms.toFixed(ms >= 100 ? 0 : 1)} ms`;
}

export function fmtDb(db: number): string {
  return db <= -95 ? "-inf dB" : `${db > 0 ? "+" : ""}${db.toFixed(1)} dB`;
}

export function fmtPct(t: number): string {
  return `${Math.round(t * 100)} %`;
}
